/**
 * Utility functions for timezone handling and local time formatting
 */

/**
 * Formats a user's local time based on their timezone information
 * @param tz - Timezone identifier (e.g., "America/Los_Angeles")
 * @param tzOffset - Timezone offset from UTC in seconds
 * @returns Formatted time string (e.g., "3:45 PM PDT")
 */
export function formatUserLocalTime(tz?: string, tzOffset?: number): string {
  if (!tz && typeof tzOffset !== 'number') {
    return '';
  }

  const now = new Date();
  
  if (tz) {
    try {
      // Use the timezone identifier if available (most accurate)
      return now.toLocaleTimeString('en-US', {
        timeZone: tz,
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      });
    } catch (error) {
      // Fall back to offset calculation if timezone is invalid
      console.warn(`Invalid timezone: ${tz}, falling back to offset calculation`);
    }
  }
  
  if (typeof tzOffset === 'number') {
    // Calculate time using UTC offset (tzOffset is in seconds)
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
    const localTime = new Date(utcTime + (tzOffset * 1000));
    
    return localTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  }
  
  return '';
}

/**
 * Gets a short timezone abbreviation from timezone label
 * @param tzLabel - Full timezone label (e.g., "Pacific Daylight Time")
 * @returns Short abbreviation (e.g., "PDT") or empty string
 */
export function getTimezoneAbbreviation(tzLabel?: string): string {
  if (!tzLabel) return '';
  
  // Common timezone abbreviation mappings
  const abbreviations: { [key: string]: string } = {
    'Pacific Standard Time': 'PST',
    'Pacific Daylight Time': 'PDT',
    'Mountain Standard Time': 'MST',
    'Mountain Daylight Time': 'MDT',
    'Central Standard Time': 'CST',
    'Central Daylight Time': 'CDT',
    'Eastern Standard Time': 'EST',
    'Eastern Daylight Time': 'EDT',
    'Greenwich Mean Time': 'GMT',
    'British Summer Time': 'BST',
    'Central European Time': 'CET',
    'Central European Summer Time': 'CEST',
    'Japan Standard Time': 'JST',
    'China Standard Time': 'CST',
    'Australian Eastern Standard Time': 'AEST',
    'Australian Eastern Daylight Time': 'AEDT'
  };
  
  return abbreviations[tzLabel] || '';
}

/**
 * Calculates the time difference between two timezones in hours
 * @param fromTzOffset - Source timezone offset in seconds
 * @param toTzOffset - Target timezone offset in seconds
 * @returns Time difference in hours (positive means target is ahead)
 */
export function getTimezoneDifference(fromTzOffset?: number, toTzOffset?: number): number {
  if (typeof fromTzOffset !== 'number' || typeof toTzOffset !== 'number') {
    return 0;
  }
  
  return (toTzOffset - fromTzOffset) / 3600; // Convert seconds to hours
}

/**
 * Determines if it's likely daytime or nighttime for a user based on their timezone
 * @param tz - Timezone identifier
 * @param tzOffset - Timezone offset in seconds
 * @returns 'day' | 'night' | 'unknown'
 */
export function getDayNightStatus(tz?: string, tzOffset?: number): 'day' | 'night' | 'unknown' {
  if (!tz && typeof tzOffset !== 'number') {
    return 'unknown';
  }
  
  const now = new Date();
  let localTime: Date;
  
  if (tz) {
    try {
      // Get the hour in the user's timezone
      const timeString = now.toLocaleString('en-US', { 
        timeZone: tz, 
        hour12: false,
        hour: '2-digit'
      });
      const hour = parseInt(timeString);
      return (hour >= 6 && hour < 18) ? 'day' : 'night';
    } catch (error) {
      // Fall back to offset calculation
    }
  }
  
  if (typeof tzOffset === 'number') {
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
    localTime = new Date(utcTime + (tzOffset * 1000));
    const hour = localTime.getHours();
    return (hour >= 6 && hour < 18) ? 'day' : 'night';
  }
  
  return 'unknown';
}

/**
 * Determines if a user is in Mountain Time (MDT/MST) and should NOT show a clock badge
 * @param tz - Timezone identifier
 * @param tzLabel - Timezone label
 * @param tzOffset - Timezone offset in seconds
 * @returns true if user is in Mountain Time (local), false if remote
 */
export function isInMountainTime(tz?: string, tzLabel?: string, tzOffset?: number): boolean {
  if (!tz && !tzLabel && typeof tzOffset !== 'number') {
    return false; // Unknown timezone, assume remote
  }
  
  // Check timezone identifier
  if (tz) {
    const mountainTimezones = [
      'America/Denver',
      'America/Edmonton', 
      'America/Boise',
      'America/Phoenix', // MST year-round
      'US/Mountain'
    ];
    if (mountainTimezones.includes(tz)) {
      return true;
    }
  }
  
  // Check timezone label
  if (tzLabel) {
    const mountainLabels = [
      'Mountain Standard Time',
      'Mountain Daylight Time',
      'MST',
      'MDT'
    ];
    if (mountainLabels.some(label => tzLabel.includes(label))) {
      return true;
    }
  }
  
  // Check timezone offset (MDT is -6 hours = -21600 seconds, MST is -7 hours = -25200 seconds)
  if (typeof tzOffset === 'number') {
    return tzOffset === -21600 || tzOffset === -25200;
  }
  
  return false;
}

/**
 * Formats a compact time display for the clock badge (e.g., "2:45" or "14:30")
 * @param tz - Timezone identifier
 * @param tzOffset - Timezone offset in seconds
 * @returns Compact time string
 */
export function formatCompactTime(tz?: string, tzOffset?: number): string {
  if (!tz && typeof tzOffset !== 'number') {
    return '';
  }

  const now = new Date();
  
  if (tz) {
    try {
      return now.toLocaleTimeString('en-US', {
        timeZone: tz,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).replace(/\s?(AM|PM)/, ''); // Remove AM/PM for compact display
    } catch (error) {
      // Fall back to offset calculation
    }
  }
  
  if (typeof tzOffset === 'number') {
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
    const localTime = new Date(utcTime + (tzOffset * 1000));
    
    return localTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).replace(/\s?(AM|PM)/, ''); // Remove AM/PM for compact display
  }
  
  return '';
}